import React, { useEffect, useRef, useState, ChangeEvent } from "react"

import TextField from "@material-ui/core/TextField"
import InputAdornment from "@material-ui/core/InputAdornment"
import Slider from "@material-ui/core/Slider"

import useMediaQuery from "@material-ui/core/useMediaQuery"
import { round, toSlider, fromSlider } from "@ecom/ui/components/Slider/utils"
import clsx from "clsx"
import { InputSliderProps } from "../types"
import { formatNumber, getFormattedValue } from "../helpers"
import { DLClickEventExtended } from "../../../../helpers/WatcherDL/DLEvents"

import * as styles from "./inputSlider.module.scss"

export const InputSlider = ({ inputLabel, sum, minNum, maxNum, dispatch }: InputSliderProps) => {
  const isMobile = useMediaQuery("(max-width:599.5px)")
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputMode, setInputMode] = useState(false)
  const [inputValue, setInputValue] = useState(String(sum))

  useEffect(() => {
    if (!inputMode) {
      setInputValue(String(sum))
    }
  }, [sum, inputMode])

  useEffect(() => {
    if (inputMode) {
      inputRef.current?.click()
    }
  }, [inputMode])

  const handleChangeCommittedSlider = (_: ChangeEvent<{}>, newValue: number | number[]) => {
    const numericValue = typeof newValue === "number" ? newValue : newValue[0]
    const roundValue = round(fromSlider(numericValue))

    DLClickEventExtended({
      name: inputLabel,
      eventCategory: "monthly_spending_calc",
      eventAction: String(roundValue),
      placement: "calculator",
    })
  }

  const selectAll = () => {
    if (isMobile) {
      setInputValue(String(sum))
      inputRef.current?.select()
    }
  }

  const showInput = () => {
    setInputMode(true)
  }

  const hideInput = () => {
    setInputMode(false)

    const numericValue = Number(inputValue)

    if (numericValue < minNum) {
      dispatch({ type: "SET_SUM", payload: minNum })
      setInputValue(String(minNum))
    } else if (numericValue > maxNum) {
      dispatch({ type: "SET_SUM", payload: maxNum })
      setInputValue(String(maxNum))
    } else {
      dispatch({ type: "SET_SUM", payload: numericValue })
      setInputValue(String(numericValue))
    }
  }

  const handleChangeTextField = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    if (value === "") {
      setInputValue("")
      return
    }

    if (/^[0-9]+$/.test(value) && Number(value) <= 999999) {
      setInputValue(value)
    }
  }

  const handleChangeSlider = (_: ChangeEvent<{}>, newValue: number | number[]) => {
    const roundValue = round(fromSlider(+newValue!))

    if (roundValue !== sum) {
      dispatch({ type: "SET_SUM", payload: roundValue })
    }
  }

  return (
    <>
      <p className={clsx(styles.inputLabel)}>{inputLabel}</p>
      <div className={styles.input}>
        {inputMode ? (
          <TextField
            inputRef={inputRef}
            fullWidth
            autoFocus
            onChange={handleChangeTextField}
            onTouchStart={() => selectAll()}
            onClick={() => selectAll()}
            onBlur={hideInput}
            value={inputValue}
            variant="standard"
            InputProps={{
              className: styles.inputValue,
              disableUnderline: true,
              endAdornment: (
                <InputAdornment
                  position="end"
                  classes={{
                    root: styles.inputAdornmentRoot,
                  }}
                >
                  ₽
                </InputAdornment>
              ),
              inputProps: {
                inputMode: "numeric",
                pattern: "[0-9]*",
                style: {
                  width: `${Math.max(String(inputValue).length * 11, 12)}px`,
                },
              },
            }}
          />
        ) : (
          <div
            style={{ height: 24 }}
            role="button"
            tabIndex={0}
            onClick={showInput}
            onKeyDown={showInput}
          >
            <span className={styles.inputValue}>{getFormattedValue(sum)}&nbsp;₽</span>
          </div>
        )}
        <div className={styles.sliderWrap}>
          <Slider
            min={toSlider(minNum)}
            max={toSlider(maxNum)}
            value={toSlider(sum)}
            onChange={handleChangeSlider}
            onChangeCommitted={handleChangeCommittedSlider}
            step={0.01}
            classes={{
              root: styles.sliderRoot,
              track: styles.sliderTrack,
              thumb: styles.sliderThumb,
              rail: styles.sliderRail,
              mark: styles.sliderMark,
            }}
          />
        </div>
      </div>
      <div className={clsx(styles.minMaxBlock, styles.minMaxFirstBlock)}>
        <div>{formatNumber(minNum)}</div>
        <div>{formatNumber(maxNum)}</div>
      </div>
    </>
  )
}
